import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation } from 'react-apollo';
import { Link } from 'react-router-dom';
import moment from 'moment';
import PropTypes from 'prop-types';
import LoadingOverlay from 'react-loading-overlay';
import ReactImageFallback from 'react-image-fallback';
import { useToasts } from 'react-toast-notifications';
import { Input, FormGroup, Button } from 'reactstrap';
import {
  EMPTY_MESSAGE_ICON, loaderStyle, OFFER_STATUS, FALLBACK_ICON, getLocalUserData, handleGraphqlError,
  isSameDay, routeConstants, CHAT_MESSAGE_TYPE, getMediaRenderTag, CHAT_CONSTANTS, renderDateRange,
} from '../../../../utils';
import { GET_OFFER_DETAIL_QUERY, GET_CHAT_ROOM, GET_PENDING_CONTRACT_ARTIST } from '../../../../graphql-client/queries';
import { UPDATE_OFFER_PRICE, SEND_OFFER_CHAT_MOTIFICATION } from '../../../../graphql-client/mutations';
import { ArtistDetail } from '../../../modals';
import SendMedia from './SendMedia';
import RenderChatMessages from './RenderChatMessages';
import MultiProfileListing from './MultiProfileListing';
import OfferPriceUpdate from './OfferPriceUpdate';
import { mapChatData, sendMessage } from '../../../../utils/chatUtils';
import { myFirestore, fieldPath } from '../../../../config/firebase';
import { useChatMessageInitializer } from '../../../../utils/chatMessageInitializer';

function MessageContainer(props) {
  const { getMessages } = useChatMessageInitializer();
  const [messageLoading, setMessageLoading] = useState(false);
  const chatWindowRef = useRef();
  const userData = getLocalUserData();
  const {
    isShow, onClose, messageModalObj: { artistDetail, offerId, isBroadCast = false }, offerMaxDate,
  } = props;
  const { addToast } = useToasts();
  const [offerData, setOfferData] = useState(null);
  const [artistDetailModal, setArtistDetailModal] = useState(null);
  const [chatMessage, setChatMessage] = useState('');
  const [chatRoomId, setChatRoomId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [mediaUrls, setMediaUrls] = useState([]);
  const [sendingMessage, setSendingMessage] = useState(false);

  const [chatMedia, setChatMedia] = useState([]);
  const [uploadingMessage, setUploadingMessage] = useState(false);
  const [uploadChatMedia, setUploadChatMedia] = useState([]);

  const { loading: getOfferLoading, data: offerApiData, refetch: refetchOffer } = useQuery(GET_OFFER_DETAIL_QUERY,
    { variables: { offerId, artistId: isBroadCast ? null : artistDetail[0]._id } });

  const { data: chatRoomData, loading: chatRoomLoading } = useQuery(GET_CHAT_ROOM,
    { variables: { offerId, participants: artistDetail.map((el) => el._id), chatInitiator: userData._id } });

  const [updateOfferPrice, { loading: updateBasePriceLoading }] = useMutation(UPDATE_OFFER_PRICE, {
    variables: {},
    refetchQueries: [{ query: GET_PENDING_CONTRACT_ARTIST, variables: { offerId } }],
    onError(error) {
      addToast(handleGraphqlError(error), { appearance: 'error' });
    },
    onCompleted(data) {
      addToast(data.offerPriceUpdate.message, { appearance: 'success' });
    },
  });
  const [sendOfferChatNotification] = useMutation(SEND_OFFER_CHAT_MOTIFICATION, {
    variables: {},
  });

  useEffect(() => {
    refetchOffer({ variables: { offerId, artistId: isBroadCast ? null : artistDetail[0]._id } });
  },
  [artistDetail]);

  const scrollToBottom = () => {
    chatWindowRef && chatWindowRef.current && chatWindowRef.current.scrollTo(0, chatWindowRef.current.scrollHeight);
  };

  useEffect(() => {
    setMessageLoading(true);
  }, []);

  useEffect(() => {
    chatMessages.length > 0 && scrollToBottom();
  }, [chatMessages]);

  useEffect(() => {
    offerApiData && setOfferData(offerApiData.offerDetail.data);
  }, [offerApiData]);

  const getChatMessages = async () => {
    const messages = await getMessages(chatRoomData.getChatRoom, userData._id);
    setChatMessages(mapChatData(userData, messages, isBroadCast));
    setMessageLoading(false);
  };

  const uploadMedia = ((e) => {
    const file = e.target.files[0];
    const image = getMediaRenderTag(file);
    const now = Date.now();
    setChatMedia([...chatMedia, {
      id: now, image, file, type: file.type,
    }]);
  });


  const onNewMessage = () => myFirestore
    .collection(`${CHAT_CONSTANTS.MESSAGE_BOARD}`).where(fieldPath.documentId(), 'in', chatRoomData.getChatRoom.map((el) => el._id))
    .onSnapshot((snapshot) => {
      snapshot.docChanges().forEach(() => {
        getChatMessages();
      });
    });

  useEffect(() => {
    let newMessageSub = () => {};
    if (chatRoomData && chatRoomData.getChatRoom.length > 0) {
      setChatRoomId(chatRoomData.getChatRoom[0]._id);
      newMessageSub = onNewMessage();
      getChatMessages();
      setChatMessage('');
      setChatMedia([]);
    }
    return () => {
      newMessageSub();
    };
  }, [chatRoomData]);

  const sendChatMessage = async (message) => {
    setSendingMessage(true);
    const artists = isBroadCast ? artistDetail.map((el) => el._id) : artistDetail[0]._id;
    await sendMessage(userData._id, artists, message, chatRoomData.getChatRoom, offerId, isBroadCast,
      true);
    setSendingMessage(false);
    getChatMessages();
    if (isBroadCast) {
      artistDetail.forEach((el) => sendOfferChatNotification({ variables: { offerId, receiverId: el._id } }));
    } else { sendOfferChatNotification({ variables: { offerId, receiverId: artistDetail[0]._id } }); }
  };

  const handleSendMessage = async (message) => {
    if (chatMedia.length === 0) {
      if (message) {
        sendChatMessage([{ message, type: CHAT_MESSAGE_TYPE.TEXT }]);
        setChatMedia([]);
        setChatMessage(null);
      }
      return;
    }
    setUploadingMessage(true);
    let upChatMedia = chatMedia;
    if (message) {
      upChatMedia = [...upChatMedia, { message, type: CHAT_MESSAGE_TYPE.TEXT }];
    }
    setUploadChatMedia(upChatMedia);
    setChatMedia([]);
    setChatMessage(null);
    scrollToBottom();
  };

  const mediaUploadComplete = (id, url, file) => {
    mediaUrls.push({
      id,
      message: url,
      type: file.type,
      size: file.size,
      name: file.name,
    });
    const filterMedia = uploadChatMedia.filter((el) => mediaUrls.findIndex((dl) => dl.id === el.id) === -1);

    setMediaUrls(mediaUrls);
    if (filterMedia.length === 0 || (filterMedia.length === 1 && filterMedia[0].type === 'text')) {
      const sendMesg = filterMedia[0] && filterMedia[0].message ? [...mediaUrls, filterMedia[0]] : mediaUrls;
      sendChatMessage(sendMesg);
      setUploadChatMedia([]);
      setChatMessage(null);
      setMediaUrls([]);
    }
  };

  const onKeyPress = (event) => {
    if (event.key === 'Enter') {
      !sendingMessage && handleSendMessage(event.target.value);
    }
  };
  const handleChange = (event) => {
    setChatMessage(event.target.value);
  };


  const renderMediaSending = (media) => media && media.map((el) => (
    <div className="admin-message-dashboard-row" key={el.id || Math.random()}>
      <div className="admin-message-dashboard-text send">
        <SendMedia data={el} mediaUploadComplete={mediaUploadComplete} scrollToBottom={scrollToBottom} />
      </div>
    </div>
  ));

  return (
    <>
      { offerData && (
      <div className={`admin-message-wrapper ${isShow ? 'show' : 'hide'}`}>
        <LoadingOverlay
          active={getOfferLoading || chatRoomLoading || updateBasePriceLoading || messageLoading}
          spinner
          text=""
          className="chatLoader"
          styles={loaderStyle}
        />
        <div className="admin-message-head">
          <div className="admin-message-head-heading">
            <p>{offerData.clientName}</p>
            <h2>
              {offerData.title}
            </h2>
            <div className="admin-message-head-heading-date">
              <h4>
                {renderDateRange(offerData.offerMinDate, offerData.offerMaxDate)}
              </h4>
              {[OFFER_STATUS.CONTRACT_ACCEPTED, OFFER_STATUS.COMPLETED, OFFER_STATUS.PUBLISHED].indexOf(offerData.status) > -1
               && (
               <span className="status-text">
                 <Link to={`${routeConstants.ADMIN_CAMPAIGN}/${offerId}/${artistDetail[0]._id}`} target="_blank">Current campaign status</Link>
               </span>
               )}

              {[OFFER_STATUS.ADMIN_ACCEPTED, OFFER_STATUS.ARTIST_ACCEPTED].indexOf(offerData.status) > -1
            && !isBroadCast && (
              <OfferPriceUpdate
                artistDetail={artistDetail[0]}
                updateOfferPrice={updateOfferPrice}
                offerId={offerId}
                offerMaxDate={offerMaxDate}
              />
              )}
            </div>
          </div>
          {isBroadCast ? <MultiProfileListing artistDetail={artistDetail} /> : (
            <div className="admin-message-head-profile">
              <span>
                <ReactImageFallback
                  src={artistDetail[0].artistImage}
                  fallbackImage={FALLBACK_ICON}
                  alt="profile"
                  className="image-cover"
                />
              </span>
              <button type="button" onClick={() => setArtistDetailModal(!artistDetailModal)}>
                View Artist’s profile
              </button>
            </div>
          )}
        </div>
        {chatMessages.length <= 0 ? (
          <div className="admin-message-dashboard empty">
            {!messageLoading && (
            <div className="empty-wrapper">
              <img
                src={EMPTY_MESSAGE_ICON}
                alt="Message"
              />
              <span>No Conversations yet</span>
            </div>
            )}
          </div>
        ) : (
          <>
            <div className="admin-message-dashboard" ref={chatWindowRef}>
              { chatMessages && chatMessages.map((el) => (
                <div key={el.date} className="send-media-row">
                  <div className="admin-message-dashboard-date">
                    <h4>{isSameDay(el.date, new Date()) ? 'Today' : el.date}</h4>
                  </div>
                  <RenderChatMessages messages={el.messages} senderDetail={artistDetail[0]} />
                </div>
              ))}
              {uploadingMessage && renderMediaSending(uploadChatMedia)}
            </div>
            <div> </div>
          </>
        )}

        <div className="admin-message-input">
          <div className="media-thumb">
            {!sendingMessage && chatMedia && chatMedia.map((el) => (
              <div className="thumb-box" key={el.id}>
                {el.type !== 'text' && (
                <img
                  className="image-cover"
                  src={el.image}
                  alt="media"
                />
                )}
                <button
                  type="button"
                  className="icon icon-cross"
                  onClick={() => {
                    setChatMedia(chatMedia.filter((cm) => cm.id !== el.id));
                  }}
                />
              </div>
            ))}
          </div>
          <FormGroup>
            <Input
              type="text"
              placeholder="Write your Message here"
              value={chatMessage || ''}
              onChange={handleChange}
              onKeyPress={onKeyPress}
            />
            <label htmlFor="mediaFile">
              <input name="mediaFile" id="mediaFile" type="file" onChange={uploadMedia} />
            </label>
          </FormGroup>
          <Button
            type="button"
            disabled={(!chatMessage && !chatMedia) || !chatRoomId
             || (moment(new Date()).isAfter(offerMaxDate))}
            className="send-btn"
            onClick={() => handleSendMessage(chatMessage)}
          />
        </div>
      </div>
      )}
      <span
        onClick={onClose}
        className="backdrop"
      />

      {artistDetailModal && !isBroadCast && (
      <ArtistDetail
        isOpen={artistDetailModal}
        onClose={() => setArtistDetailModal(!artistDetailModal)}
        userId={artistDetail[0]._id}
        key={Math.random()}
        canNavToOffer={false}
      />
      )}
    </>
  );
}
MessageContainer.propTypes = {
  isShow: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  offerMaxDate: PropTypes.string.isRequired,
  messageModalObj: PropTypes.shape({
    offerId: PropTypes.string,
    isBroadCast: PropTypes.bool,
    artistDetail: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      artistImage: PropTypes.string,
      _id: PropTypes.string,
      basePrice: PropTypes.number,
      timeLine: PropTypes.arrayOf(PropTypes.shape({ status: PropTypes.string, createdAt: PropTypes.string })),
    })),
  }).isRequired,
};

export default MessageContainer;
